import axios from 'axios';
import i18n from 'i18next';
import { useNavigate } from 'react-router';
import React, { useEffect, useState } from 'react';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { AdditionalImagesUpload, MainImageUpload, useImageHandling } from './ImageProcessor';
import i18next from 'i18next';
import ProductAttributesSelector from './ProductAttributesSelector';
import { useTranslation } from 'react-i18next';
import { InformationCircleIcon } from '@heroicons/react/24/solid';
import MessageAlerts from './MessageAlerts';

export default function CreateProd() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const localhost = process.env.REACT_APP_LOCALHOST;
  const token = localStorage.getItem('token');
  const [categories, setCategories] = useState([]);
  const [nameEn, setNameEn] = useState('');
  const [nameFr, setNameFr] = useState('');
  const [descriptionEn, setDescriptionEn] = useState('');
  const [descriptionFr, setDescriptionFr] = useState('');
  const [price, setPrice] = useState('');
  const [stock, setStock] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [forValue, setForValue] = useState("all");

  const [sizes, setSizes] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [allOptions, setAllOptions] = useState([]);

  const [colors, setColors] = useState([]);
  const [selectedColor, setSelectedColor] = useState([]);
  const [allColors, setAllColors] = useState([]);
  const [loadingProcess, setLoadingProcess] = useState(false);

  const [errorMessages, setErrorMessages] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  const { canvasRef, mainImage, additionalImages, handleMainImageUpdate, handleAdditionalImagesUpdate, handleMainImageRemove, handleAdditionalImageRemove, getAllImages } = useImageHandling();

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await axios.get(localhost + 'admin/categories', {
          headers: { Authorization: `Bearer ${token}` },
        });
        setCategories(res.data);
      } catch (err) {
        // Handle error
      }
    };

    const fetchColors = async () => {
      try {
        const res = await axios.get(localhost + 'all-colors');
        setColors(res.data);
        setAllColors([
          { value: 'select_all', label: 'Select All' }, ...res.data.map((item) => ({ value: item.id, label: item.name[i18next.language] })),
        ]);
      } catch (err) {
        // Handle error
      }
    };

    const fetchSizes = async () => {
      try {
        const res = await axios.get(localhost + 'all-sizes');
        setSizes(res.data);
        setAllOptions([
          { value: 'select_all', label: 'Select All' }, ...res.data.map((item) => ({ value: item.id, label: item.name })),
        ]);
        setSelectedOptions(res.data.map((item) => ({ value: item.id, label: item.name })));
      } catch (err) {
        // Handle error
      }
    };
    fetchCategories();
    fetchColors();
    fetchSizes();
  }, [localhost, token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = new FormData();

    // Add all basic form fields
    form.append('name[en]', nameEn);
    form.append('name[fr]', nameFr);
    form.append('description[en]', descriptionEn);
    form.append('description[fr]', descriptionFr);
    form.append('price', price);
    form.append('stock', stock);
    form.append('category_id', categoryId);
    form.append('gender', forValue);
    selectedOptions.forEach((id) => form.append('sizes[]', id.value));
    selectedColor.forEach((id) => form.append('colors[]', id.value));

    const allImages = getAllImages();
    allImages.forEach((file, index) => {
      form.append('imgs[]', file);
    });

    try {
      const res = await axios.post(localhost + 'admin/create-product', form, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
      });
      setSuccessMessage('Product successfully created!');
      setErrorMessages('');

      // Process of stock :
      setLoadingProcess(true);
      await axios.post(`${localhost}admin/create-stock-combinations/${res.data}`, { pre_stock: stock }, { headers: { Authorization: `Bearer ${token}` } })
        .then(() => {
          setTimeout(() => {
            navigate(`/admin/stock-management/${res.data}`);
          }, 1000);
        }).catch((err) => {
          // console.error(err);
          // setErrorMessage('Error creating product: ' + (err.response?.data?.error || err.message));
          setSuccessMessage('');
        });
    } catch (err) {
      console.error(err);
      const errJson = err ? JSON.parse(err.request?.response) : null;
      if (errJson && errJson.error) {
        setErrorMessages(errJson.error);
      } else {
        setErrorMessages({ general: ['An unexpected error occurred.'] });
      }
      setSuccessMessage('');
    }
  };

  if (loadingProcess) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="loading-spinner" />
      </div>
    )
  }

  return (
    <div className="antialiased">
      <div className="max-w-7xl mx-auto">
        <div className="w-full flex justify-start mb-2">
          <a href="/admin/products" className="flex gap-1 items-center text-sm bg-black hover:bg-black/80 text-white pl-2 pr-3 py-2 rounded-md">
            <ArrowLeftIcon className="size-4" /> {t("back")}
          </a>
        </div>
        <div className="bg-white shadow-sm ring-1 ring-gray-900/5 sm:rounded-xl">
          <form onSubmit={handleSubmit} className="px-4 py-6 sm:p-8">
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-semibold text-gray-900">Create New Product</h2>
                <p className="mt-1 text-sm text-gray-600">Fill in the details below to create a new product listing.</p>
              </div>
              <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
                <div>
                  <label className="text-sm font-medium text-gray-700">English Name</label>
                  <input name="product-name" type="text" value={nameEn} onChange={(e) => setNameEn(e.target.value)} className="mt-1 block w-full rounded-md py-1.5 px-2 border border-gray-300" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">French Name</label>
                  <input name="product-name" type="text" value={nameFr} onChange={(e) => setNameFr(e.target.value)} className="mt-1 block w-full rounded-md py-1.5 px-2 border border-gray-300" />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-sm font-medium text-gray-700">English Description</label>
                  <textarea name='description' value={descriptionEn} onChange={(e) => setDescriptionEn(e.target.value)} rows={3} className="mt-1 block w-full rounded-md py-1.5 px-2 border border-gray-300" />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-sm font-medium text-gray-700">French Description</label>
                  <textarea name='description' value={descriptionFr} onChange={(e) => setDescriptionFr(e.target.value)} rows={3} className="mt-1 block w-full rounded-md py-1.5 px-2 border border-gray-300" />
                </div>
              </div>
              <div className="w-full">
                <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Price</label>
                    <input name='price' type="number" value={price} onChange={(e) => setPrice(e.target.value)} className="mt-1 block w-full rounded-md py-1.5 px-2 border border-gray-300" />
                  </div>
                  <div>
                    <label className="flex flex-wrap items-center gap-1 text-sm font-medium text-gray-700">
                      Stock
                      <span data-tooltip-id="my-tooltip" data-tooltip-content={t("stock-msg")}>
                        <InformationCircleIcon className='size-[18px] text-orange-600' />
                      </span>
                    </label>
                    <input type="number" value={stock} onChange={(e) => setStock(e.target.value)} className="mt-1 block w-full rounded-md py-1.5 px-2 border border-gray-300" />
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 mt-9">
                  <div>
                    <label className="font-medium text-gray-700">Gender</label>
                    <select value={forValue} onChange={(e) => setForValue(e.target.value)} className="mt-1 block w-full rounded-md py-1.5 px-2 border border-gray-300 focus:border-orange-500 focus:ring-orange-500">
                      <option value="">Select Gender</option>
                      {["all", "men", "women"].map((item, index) => (
                        <option key={index} value={item}>
                          {item}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Category</label>
                    <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} className="mt-1 block w-full rounded-md py-1.5 px-2 border border-gray-300">
                      <option value="">Select Category</option>
                      {categories.map((item, index) => (
                        <option key={index} value={item.id}>
                          {item.name[i18n.language]}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className='mt-9'>
                  <ProductAttributesSelector
                    sizes={sizes}
                    colors={colors}
                    selectedSizes={selectedOptions}
                    selectedColors={selectedColor}
                    onSizesChange={setSelectedOptions}
                    onColorsChange={setSelectedColor}
                  />
                </div>
              </div>

              <div className="space-y-4">
                <canvas ref={canvasRef} className="hidden" />
                <MainImageUpload mainImage={mainImage} onImageUpdate={handleMainImageUpdate} onImageRemove={handleMainImageRemove} />
                <AdditionalImagesUpload additionalImages={additionalImages} onImagesUpdate={handleAdditionalImagesUpdate} onImageRemove={handleAdditionalImageRemove} />
              </div>

              <MessageAlerts
                successMessage={successMessage}
                errorMessages={errorMessages}
              />

              <div className="mt-6 flex items-center justify-end gap-x-6">
                <button type="submit" className="rounded-md bg-black hover:bg-black/80 px-6 py-2 text-sm font-semibold text-white">
                  Create Product
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}